/**
 * Reviews → the "Do not send" list, the Upcoming tab's review-state chips, and the
 * "Who gets asked" send rules.
 *
 * ⛔ IMPORT-FREE ON PURPOSE. cypress/node-tests/reviews-do-not-send.test.js strips the
 * `export`s and executes THIS file, so the test can never drift from what ships.
 * Only `export const` / `export function` here — no imports, no default export.
 * Anything that needs moment or the site timezone is INJECTED as a formatter.
 *
 * The contract (reviews-service, migration 004):
 *   - opt-out reasons: complaint | staff_family | dispute | patient_request | other
 *   - durations: forever | 30d | 90d | until (+ untilDate 'YYYY-MM-DD', a day AFTER today)
 *   - note: ≤ 280 characters, required when the reason is "other"
 *   - expiresAt: ISO-8601 UTC, or null for Forever
 *   - skip/block reason codes: see SKIP_REASON_LABELS
 *   - settings.sendRules: { frequencyCapDays: 0|30|60|90|180|365, suppressAfterLowRating }
 */

export const NOTE_MAX = 280;

export const OPTOUT_REASONS = [
  { value: 'complaint', label: 'Complaint' },
  { value: 'staff_family', label: 'Staff or family' },
  { value: 'dispute', label: 'Billing or legal dispute' },
  { value: 'patient_request', label: 'Patient asked' },
  { value: 'other', label: 'Other' },
];

export const DURATIONS = [
  { value: 'forever', label: 'Forever' },
  { value: '30d', label: '30 days' },
  { value: '90d', label: '90 days' },
  { value: 'until', label: 'Until a date' },
];

export const DO_NOT_SEND_SOURCES = [
  { value: 'all', label: 'All' },
  { value: 'staff', label: 'Added by staff' },
  { value: 'stop', label: 'Replied STOP' },
];

export const FREQUENCY_CAP_OPTIONS = [
  { value: 0, label: 'Off' },
  { value: 30, label: '30 days' },
  { value: 60, label: '60 days' },
  { value: 90, label: '90 days' },
  { value: 180, label: '6 months' },
  { value: 365, label: '1 year' },
];

export const DEFAULT_SEND_RULES = { frequencyCapDays: 180, suppressAfterLowRating: true };

// Every code the worker / endpoints write to review_requests.SkipReason, or return as an
// Upcoming `reason`. One map so the Requests table, the Upcoming chips and the profile modal
// can never describe the same skip two different ways.
export const SKIP_REASON_LABELS = {
  opted_out: 'On Do not send list',
  replied_stop: 'Patient replied STOP',
  excluded_proc_code: 'Procedure excluded',
  frequency_cap: 'Asked recently',
  low_rating: 'Rated low before',
  open_feedback: 'Open private feedback',
  staff_skip: 'Skipped by staff',
  not_paid: 'Reviews not active',
  automation_off: 'Automation off',
  patient_not_found: 'Patient not found',
  no_channel: 'No phone or email',
  duplicate_request: 'Duplicate request for this visit',
};

const YMD_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function isOneOf(list, value) {
  return list.some((o) => o.value === value);
}

function trimmed(value) {
  return typeof value === 'string' ? value.trim() : '';
}

/** "Complaint" … — legacy rows saved before reasons existed read "No reason recorded". */
export function reasonLabel(code) {
  const hit = OPTOUT_REASONS.find((r) => r.value === code);
  return hit ? hit.label : 'No reason recorded';
}

/** A skip/block code → short label; anything unknown (or missing) reads "Skipped". */
export function skipReasonLabel(code) {
  return (code && Object.prototype.hasOwnProperty.call(SKIP_REASON_LABELS, code))
    ? SKIP_REASON_LABELS[code]
    : 'Skipped';
}

/**
 * validateDoNotSendForm(form, { todayYmd, requirePatient }) → error string | null
 *
 * form: { patNum?, reason, note, duration, untilDate }. `todayYmd` is TODAY in the SITE
 * timezone ('YYYY-MM-DD'); the until-date must be strictly after it (the server computes
 * "end of that day in the site timezone", so today would already be expired by tonight).
 */
export function validateDoNotSendForm(form, opts) {
  const f = form && typeof form === 'object' ? form : {};
  const o = opts && typeof opts === 'object' ? opts : {};

  if (o.requirePatient && (f.patNum === undefined || f.patNum === null || f.patNum === '')) {
    return 'Choose a patient.';
  }
  if (!isOneOf(OPTOUT_REASONS, f.reason)) return 'Choose a reason.';

  const note = trimmed(f.note);
  if (f.reason === 'other' && !note) return 'Add a note for “Other”.';
  if (note.length > NOTE_MAX) return 'Notes are limited to 280 characters.';

  if (!isOneOf(DURATIONS, f.duration)) return 'Choose how long.';
  if (f.duration === 'until') {
    const until = typeof f.untilDate === 'string' ? f.untilDate : '';
    if (!YMD_RE.test(until)) return 'Pick a date after today.';
    // Zero-padded YYYY-MM-DD strings compare correctly as strings.
    if (typeof o.todayYmd === 'string' && YMD_RE.test(o.todayYmd) && until <= o.todayYmd) {
      return 'Pick a date after today.';
    }
  }
  return null;
}

/**
 * buildOptOutPayload(form) → the body fields for POST /set-review-patient-optout
 * (siteID / patNum / employeeID are added by the caller). A blank note is omitted, and
 * untilDate is only sent for the 'until' duration.
 */
export function buildOptOutPayload(form) {
  const f = form && typeof form === 'object' ? form : {};
  const payload = { optedOut: true, reason: f.reason, duration: f.duration };
  const note = trimmed(f.note);
  if (note) payload.note = note;
  if (f.duration === 'until') payload.untilDate = f.untilDate;
  return payload;
}

/**
 * entryEndsByToday(entry, toSiteYmd, todayYmd) → true when an expiring entry's site-local end
 * date is TODAY or earlier. Such an entry cannot be re-saved with its own date (the server needs
 * a date strictly after today), so Edit must ask staff for a new one.
 */
export function entryEndsByToday(entry, toSiteYmd, todayYmd) {
  const e = entry && typeof entry === 'object' ? entry : {};
  if (!e.expiresAt || typeof todayYmd !== 'string' || !YMD_RE.test(todayYmd)) return false;
  const ymd = typeof toSiteYmd === 'function' ? toSiteYmd(e.expiresAt) : '';
  return typeof ymd === 'string' && YMD_RE.test(ymd) && ymd <= todayYmd;
}

/**
 * formFromEntry(entry, toSiteYmd, todayYmd?) → a form for Edit.
 * A stored entry only has an absolute expiresAt, so a 30/90-day entry re-opens as
 * "Until <that date>" — re-saving it keeps the same end date rather than restarting the clock.
 * `toSiteYmd(isoUtc)` converts the UTC instant to the site-local 'YYYY-MM-DD'.
 * An entry that ends TODAY (or earlier) re-opens as "Until" with a BLANK date: its own date would
 * fail validation on a note-only edit, and a guessed date would silently extend the pause.
 */
export function formFromEntry(entry, toSiteYmd, todayYmd) {
  const e = entry && typeof entry === 'object' ? entry : {};
  const form = {
    reason: isOneOf(OPTOUT_REASONS, e.reason) ? e.reason : '',
    note: typeof e.note === 'string' ? e.note : '',
    duration: 'forever',
    untilDate: '',
  };
  if (e.expiresAt) {
    const ymd = typeof toSiteYmd === 'function' ? toSiteYmd(e.expiresAt) : '';
    if (typeof ymd === 'string' && YMD_RE.test(ymd)) {
      form.duration = 'until';
      form.untilDate = entryEndsByToday(e, toSiteYmd, todayYmd) ? '' : ymd;
    }
  }
  return form;
}

// Server 400 codes from POST /set-review-patient-optout → the same office-facing sentences the
// form's own validation uses. The server's message is developer phrasing, so it is never shown.
export const OPTOUT_ERROR_MESSAGES = {
  INVALID_REASON: 'Choose a reason.',
  NOTE_REQUIRED: 'Add a note for “Other”.',
  NOTE_TOO_LONG: 'Notes are limited to 280 characters.',
  INVALID_DURATION: 'Choose how long.',
  INVALID_UNTIL_DATE: 'Pick a date after today.',
};

export const OPTOUT_ERROR_GENERIC = "Couldn't save — try again.";

/** optOutErrorMessage(code, fallback?) → a known code's sentence, else fallback, else the generic one. */
export function optOutErrorMessage(code, fallback) {
  if (typeof code === 'string' && Object.prototype.hasOwnProperty.call(OPTOUT_ERROR_MESSAGES, code)) {
    return OPTOUT_ERROR_MESSAGES[code];
  }
  return typeof fallback === 'string' && fallback ? fallback : OPTOUT_ERROR_GENERIC;
}

/**
 * errorForLog(err) → { code, status, message } — the ONLY error shape the Reviews components log.
 * An axios error (or anything carrying .config / .response) holds the request body — patNum,
 * the staff note — and console output is captured by LogRocket.
 */
export function errorForLog(err) {
  const e = err && typeof err === 'object' ? err : {};
  const status = e.status !== undefined ? e.status : (e.response && e.response.status);
  return {
    code: typeof e.code === 'string' ? e.code : undefined,
    status: typeof status === 'number' ? status : undefined,
    message: typeof e.message === 'string' ? e.message : (typeof err === 'string' ? err : undefined),
  };
}

/**
 * doNotSendRowKey(row) → 'source-siteID-patNum'. A multi-clinic office can list the same PatNum
 * under two clinic SiteIDs, so patNum (or source-patNum) alone is not a row identity.
 */
export function doNotSendRowKey(row) {
  const r = row && typeof row === 'object' ? row : {};
  return `${r.source}-${r.siteID}-${r.patNum}`;
}

/**
 * pageOffsetAfterEmpty(offset, total, pageSize) → the offset to reload, or null when no step is
 * needed. A page that came back EMPTY past the first page (its last row was just removed) steps
 * back to the last page that still has rows — never below 0.
 */
export function pageOffsetAfterEmpty(offset, total, pageSize) {
  const off = Number.isInteger(offset) ? offset : 0;
  const size = Number.isInteger(pageSize) && pageSize > 0 ? pageSize : 50;
  if (off <= 0) return null;
  const t = Number.isInteger(total) && total > 0 ? total : 0;
  const lastPage = t > 0 ? Math.floor((t - 1) / size) * size : 0;
  return Math.max(0, Math.min(off - size, lastPage));
}

/** "Forever" | "Until Dec 13, 2026". `formatUtcToSiteDate(isoUtc)` does the TZ work. */
export function expiryLabel(expiresAt, formatUtcToSiteDate) {
  if (!expiresAt) return 'Forever';
  const text = typeof formatUtcToSiteDate === 'function' ? formatUtcToSiteDate(expiresAt) : '';
  return text ? `Until ${text}` : `Until ${String(expiresAt).slice(0, 10)}`;
}

/**
 * upcomingStateChip(row, { formatTime }) → { label, tone, detail }
 * tone ∈ teal | gray | blue | amber | neutral (maps onto the rv-status-dot colours).
 * `formatTime(siteLocalDateTime)` renders the predicted send time ("7:00 PM").
 */
export function upcomingStateChip(row, opts) {
  const r = row && typeof row === 'object' ? row : {};
  const formatTime = opts && typeof opts.formatTime === 'function' ? opts.formatTime : null;
  const detail = typeof r.detail === 'string' ? r.detail : '';

  switch (r.state) {
    case 'will_send': {
      const when = r.sendAt && formatTime ? formatTime(r.sendAt) : '';
      return { label: when ? `Will be asked ${when}` : 'Will be asked', tone: 'teal', detail };
    }
    case 'skipped': {
      if (!r.reason || r.reason === 'staff_skip') {
        return {
          label: r.skippedByName ? `Skipped by ${r.skippedByName}` : 'Skipped by staff',
          tone: 'gray',
          detail,
        };
      }
      return { label: skipReasonLabel(r.reason), tone: 'gray', detail };
    }
    case 'already_requested':
      return { label: 'Already asked for this visit', tone: 'blue', detail };
    case 'blocked': {
      // duplicate_request is quiet too: the visit's request already went out, nothing to fix.
      const quiet = ['opted_out', 'replied_stop', 'automation_off', 'not_paid', 'duplicate_request'];
      return {
        label: r.reason === 'replied_stop' ? 'Replied STOP' : skipReasonLabel(r.reason),
        tone: quiet.indexOf(r.reason) > -1 ? 'gray' : 'amber',
        detail,
      };
    }
    default:
      return { label: 'Unknown', tone: 'neutral', detail };
  }
}

/**
 * buildUpcomingDays(todayYmd, count = 8) → [{ ymd, label, sub }]
 * Today, Tomorrow, then "Wed" / "Sep 16" … through today + 7. `todayYmd` is today in the
 * SITE timezone; the arithmetic is pure calendar math (UTC dates), so no browser TZ leaks in.
 */
export function buildUpcomingDays(todayYmd, count) {
  const m = typeof todayYmd === 'string' ? YMD_RE.exec(todayYmd) : null;
  if (!m) return [];
  const n = Number.isInteger(count) && count > 0 ? count : 8;
  const base = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const pad = (v) => (v < 10 ? '0' + v : String(v));
  const out = [];
  for (let i = 0; i < n; i++) {
    const d = new Date(base + i * 86400000);
    const ymd = `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
    const monthDay = `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`;
    let label;
    if (i === 0) label = 'Today';
    else if (i === 1) label = 'Tomorrow';
    else label = WEEKDAYS[d.getUTCDay()];
    out.push({ ymd, label, sub: monthDay });
  }
  return out;
}

/**
 * sendRulesForForm(sendRules) → a NEW { frequencyCapDays, suppressAfterLowRating }
 * Defaults (6 months, on) fill only ABSENT keys, so a bad stored value still reaches
 * validateSendRules instead of being silently replaced — the timingForForm rule.
 */
export function sendRulesForForm(sendRules) {
  const s = sendRules && typeof sendRules === 'object' ? sendRules : {};
  const absent = (v) => v === undefined || v === null;
  return {
    frequencyCapDays: absent(s.frequencyCapDays) ? DEFAULT_SEND_RULES.frequencyCapDays : s.frequencyCapDays,
    suppressAfterLowRating: absent(s.suppressAfterLowRating)
      ? DEFAULT_SEND_RULES.suppressAfterLowRating
      : s.suppressAfterLowRating,
  };
}

/** Mirrors reviews-service lib/settings.js. error string | null */
export function validateSendRules(sendRules) {
  const s = sendRules && typeof sendRules === 'object' ? sendRules : null;
  if (!s) return 'Choose who gets asked.';
  if (!isOneOf(FREQUENCY_CAP_OPTIONS, s.frequencyCapDays)) {
    return 'Pick how long to wait before asking the same patient again.';
  }
  if (typeof s.suppressAfterLowRating !== 'boolean') {
    return 'Choose whether low ratings stop automatic requests.';
  }
  return null;
}
